/**
 * Whether this browser has unread Featurebase updates (changelog entries).
 *
 * The flag lives once in ConditionalFeaturebaseProvider
 * (useUnreadUpdatesTracker) and is shared through context, so the Home
 * "See what's new" button and the header messages icon always agree:
 * marking updates read from either clears both. Read state is Featurebase's
 * and lives per browser, so this is only a hint.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FeaturebaseUnreadUpdatesContext } from '../lib/featurebase-context'
import type {
  FeaturebaseChangelogApi,
  UnreadUpdatesState,
} from '../lib/featurebase-context'

// The widget reports 0 while it is still loading, so keep asking for a while
// before concluding there's nothing new.
const UNREAD_POLL_INTERVAL_MS = 500
const UNREAD_POLL_TIMEOUT_MS = 15_000

/**
 * Provider-only: polls the changelog widget for unread updates and owns the
 * shared flag. Components read it with useUnreadChangelog().
 */
export function useUnreadUpdatesTracker(
  changelog: FeaturebaseChangelogApi | null,
): UnreadUpdatesState {
  const [unreadUpdatesCount, setUnreadUpdatesCount] = useState(0)

  useEffect(() => {
    if (!changelog) {
      return
    }
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      const unreadCount = changelog.getUnviewedCount() ?? 0
      if (unreadCount > 0) {
        setUnreadUpdatesCount(unreadCount)
        window.clearInterval(timer)
      } else if (Date.now() - startedAt >= UNREAD_POLL_TIMEOUT_MS) {
        window.clearInterval(timer)
      }
    }, UNREAD_POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [changelog])

  const markUpdatesRead = useCallback(() => {
    changelog?.markAllAsViewed()
    setUnreadUpdatesCount(0)
  }, [changelog])

  return useMemo(
    () => ({
      hasUnreadUpdates: unreadUpdatesCount > 0,
      unreadUpdatesCount,
      markUpdatesRead,
    }),
    [unreadUpdatesCount, markUpdatesRead],
  )
}

export function useUnreadChangelog(): UnreadUpdatesState {
  return useContext(FeaturebaseUnreadUpdatesContext)
}
